import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@xingternal/button';

import { FollowButton } from '@/components/FollowButton';
import { CompanyCard } from '@/components/CompanyCard';
import {
  Company,
  Maybe,
  CompanyRecommendation,
  XingId,
  EntityPageSocialProofType,
} from '@/types/graphql-types';
import { trackDismissRecommendation } from '@/utils/tracking';

import styles from './RecommendedCompanies.css';

export const getRecommendedMessageReasonId = (
  proofType: EntityPageSocialProofType,
  total: number,
) => {
  const hasSingleUser = total === 1;

  if (proofType === EntityPageSocialProofType.Employees) {
    if (hasSingleUser) {
      return 'EP_COMPANY_SOCIAL_PROOF.one';
    }
    if (!hasSingleUser) {
      return 'EP_COMPANY_SOCIAL_PROOF.other';
    }
  }

  if (proofType === EntityPageSocialProofType.Followers) {
    if (hasSingleUser) {
      return 'CP_200_ONE_FOLLOWER_THIS_COMPANY';
    }
    if (!hasSingleUser) {
      return 'CP_200_FOLLOWERS_THIS_COMPANY';
    }
  }

  return 'CP_200_FOLLOWERS_THIS_COMPANY';
};
export const getRecommendedMessageReasonVariables = (
  proofType: EntityPageSocialProofType,
  users: Maybe<XingId>[],
) => {
  if (proofType === EntityPageSocialProofType.Employees) {
    if (users?.length === 1) {
      const [user] = users;
      return {
        username: user?.firstName,
      };
    }
    if (users?.length > 1) {
      return {
        numberOfContacts: users?.length,
      };
    }
  }

  if (proofType === EntityPageSocialProofType.Followers) {
    if (users?.length === 1) {
      const [user] = users;
      return {
        name: user?.firstName,
      };
    }
  }

  return {};
};

export type RecommendedCompanyCardProps = {
  recommendedCompany: CompanyRecommendation;
  onRemoveButtonClick: (companyRecommendation: Maybe<Company>) => void;
};
export const RecommendedCompanyCard: React.FC<RecommendedCompanyCardProps> = ({
  recommendedCompany,
  onRemoveButtonClick,
}) => {
  const company = recommendedCompany?.company;
  const isFollowing = company?.userContext?.followState?.isFollowing || false;

  return (
    <CompanyCard
      key={company?.id}
      company={company}
      buttons={[
        recommendedCompany?.dismissible ? (
          <Button
            key="removeButton"
            className={styles.removeButton}
            size="small"
            onClick={() => {
              trackDismissRecommendation(company?.id);
              if (onRemoveButtonClick) onRemoveButtonClick(company);
            }}
          >
            <FormattedMessage id="CP_NEWSFEED_REMOVE" />
          </Button>
        ) : null,
        <FollowButton
          key="followButton"
          className={styles.followButton}
          disabled={company?.entityPageId ? false : true}
          entityPageId={company?.entityPageId || ''}
          isFollowing={isFollowing}
          companyId={company?.id}
          followText="CP_200_FOLLOW_LINK"
        />,
      ]}
    />
  );
};

export default RecommendedCompanyCard;
